import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Award, Globe } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '../lib/api';

// ─── Default testimonial data ─────────────────────────────────────────────────
const DEFAULT_TESTIMONIALS = [
  {
    id: '1',
    client_name: 'Carlos Rodríguez',
    company: 'TechStart',
    role_es: 'CEO, TechStart',
    role_en: 'CEO, TechStart',
    quote_es: 'Mikel Media transformó nuestra presencia digital. Los resultados superaron todas nuestras expectativas. El equipo es increíblemente profesional.',
    quote_en: 'Mikel Media transformed our digital presence. The results exceeded all our expectations. The team is incredibly professional.',
    initials: 'CR',
    avatarGradient: 'linear-gradient(135deg, #7D468C, #4EBFBF)',
    tags: [{ text: 'DESTACADO', text_en: 'FEATURED', type: 'featured' }, { text: 'Branding', type: 'default' }],
    stats: [{ icon: Star, text: '5 estrellas' }, { icon: Globe, text: 'Puerto Rico' }],
  },
  {
    id: '2',
    client_name: 'Ana García',
    company: 'Boutique Coral',
    role_es: 'Directora Creativa, Boutique Coral',
    role_en: 'Creative Director, Boutique Coral',
    quote_es: 'La producción de nuestro evento corporativo fue impecable. Todos los asistentes quedaron impresionados con cada detalle.',
    quote_en: 'The production of our corporate event was flawless. All attendees were impressed with every detail.',
    initials: 'AG',
    avatarGradient: 'linear-gradient(135deg, #F2785C, #7D468C)',
    tags: [{ text: 'Eventos', text_en: 'Events', type: 'default' }, { text: 'Producción', text_en: 'Production', type: 'default' }],
    stats: [{ icon: Award, text: 'Evento Premier' }, { icon: Star, text: '5 estrellas' }],
  },
  {
    id: '3',
    client_name: 'Roberto Mendez',
    company: 'Inversiones Norte',
    role_es: 'Director General, Inversiones Norte',
    role_en: 'General Director, Inversiones Norte',
    quote_es: 'El equipo de Mikel Media entiende perfectamente las necesidades del mercado latinoamericano. Resultados extraordinarios.',
    quote_en: 'The Mikel Media team perfectly understands the needs of the Latin American market. Extraordinary results.',
    initials: 'RM',
    avatarGradient: 'linear-gradient(135deg, #4EBFBF, #233559)',
    tags: [{ text: 'Marketing', type: 'default' }, { text: 'LATAM', type: 'featured' }],
    stats: [{ icon: Globe, text: 'USA & LATAM' }, { icon: Award, text: 'Top cliente' }],
  },
];

// ─── Testimonial Stack Component ──────────────────────────────────────────────
const TestimonialStack = ({ testimonials, lang, visibleBehind = 2 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartRef = useRef(0);
  const cardRefs = useRef([]);
  const total = testimonials.length;

  const navigate = useCallback((newIndex) => {
    setActiveIndex(((newIndex % total) + total) % total);
  }, [total]);

  const handleDragStart = (e, index) => {
    if (index !== activeIndex) return;
    setIsDragging(true);
    dragStartRef.current = 'touches' in e ? e.touches[0].clientX : e.clientX;
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragOffset(x - dragStartRef.current);
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    if (Math.abs(dragOffset) > 50) navigate(activeIndex + (dragOffset < 0 ? 1 : -1));
    setIsDragging(false);
    setDragOffset(0);
  }, [isDragging, dragOffset, activeIndex, navigate]);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('touchmove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchend', handleDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!testimonials.length) return null;

  return (
    <div className="relative" style={{ paddingBottom: '3rem' }}>
      {/* Stack of cards */}
      <div style={{ position: 'relative', height: '340px' }}>
        {testimonials.map((t, index) => {
          const displayOrder = ((index - activeIndex) % total + total) % total;
          let cardStyle = {
            position: 'absolute',
            inset: 0,
            transition: isDragging && index === activeIndex ? 'none' : 'all 0.45s cubic-bezier(0.34,1.56,0.64,1)',
            cursor: index === activeIndex ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
          };

          if (displayOrder === 0) {
            cardStyle.transform = `translateX(${dragOffset}px) translateY(0) scale(1)`;
            cardStyle.opacity = 1;
            cardStyle.zIndex = total;
          } else if (displayOrder <= visibleBehind) {
            const scale = 1 - 0.04 * displayOrder;
            const ty = -14 * displayOrder;
            cardStyle.transform = `scale(${scale}) translateY(${ty}px)`;
            cardStyle.opacity = 1 - 0.25 * displayOrder;
            cardStyle.zIndex = total - displayOrder;
          } else {
            cardStyle.transform = 'scale(0.85) translateY(-40px)';
            cardStyle.opacity = 0;
            cardStyle.zIndex = 0;
            cardStyle.pointerEvents = 'none';
          }

          const quote = lang === 'en' ? t.quote_en : t.quote_es;
          const role  = lang === 'en' ? (t.role_en || t.company) : (t.role_es || t.company);

          return (
            <div
              key={t.id}
              ref={el => cardRefs.current[index] = el}
              style={cardStyle}
              onMouseDown={(e) => handleDragStart(e, index)}
              onTouchStart={(e) => handleDragStart(e, index)}
            >
              {/* Glass card */}
              <div
                style={{
                  height: '100%',
                  borderRadius: '1.25rem',
                  background: 'linear-gradient(135deg, rgba(125,70,140,0.18) 0%, rgba(78,191,191,0.12) 50%, rgba(35,53,89,0.25) 100%)',
                  backdropFilter: 'blur(24px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(160%)',
                  border: '1px solid rgba(78,191,191,0.25)',
                  boxShadow: [
                    '0 20px 60px rgba(35,53,89,0.4)',
                    '0 4px 16px rgba(125,70,140,0.15)',
                    'inset 0 1px 0 rgba(255,255,255,0.12)',
                  ].join(', '),
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '2rem',
                }}
              >
                {/* Gloss highlight */}
                <div style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: '40%',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 100%)',
                  borderRadius: '1.25rem 1.25rem 0 0',
                  pointerEvents: 'none',
                }} />

                {/* Header: avatar + name */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div style={{
                      width: '2.75rem', height: '2.75rem', borderRadius: '0.75rem',
                      background: t.avatarGradient,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 700, fontSize: '1rem',
                      fontFamily: 'Poppins, sans-serif',
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    }}>
                      {t.initials || (t.client_name ? t.client_name[0] : '?')}
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem', fontFamily: 'Poppins, sans-serif' }}>
                        {t.client_name}
                      </div>
                      <div style={{ color: 'rgba(78,191,191,0.9)', fontSize: '0.8rem', marginTop: '0.125rem', fontFamily: 'Source Sans Pro, sans-serif' }}>
                        {role}
                      </div>
                    </div>
                  </div>

                  {/* Quote */}
                  <blockquote style={{
                    color: 'rgba(255,255,255,0.88)',
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    fontStyle: 'italic',
                    fontFamily: 'Source Sans Pro, sans-serif',
                    margin: 0,
                  }}>
                    "{quote}"
                  </blockquote>
                </div>

                {/* Footer: tags + stats */}
                <div style={{
                  borderTop: '1px solid rgba(78,191,191,0.15)',
                  paddingTop: '1rem',
                  marginTop: '1.25rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {(t.tags || []).map((tag, i) => (
                      <span key={i} style={{
                        fontSize: '0.7rem',
                        padding: '0.2rem 0.6rem',
                        borderRadius: '0.4rem',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        ...(tag.type === 'featured'
                          ? { background: 'rgba(242,120,92,0.2)', color: '#F2785C', border: '1px solid rgba(242,120,92,0.35)' }
                          : { background: 'rgba(78,191,191,0.12)', color: 'rgba(78,191,191,0.9)', border: '1px solid rgba(78,191,191,0.2)' }
                        ),
                      }}>
                        {lang === 'en' && tag.text_en ? tag.text_en : tag.text}
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    {(t.stats || []).map((stat, i) => {
                      const Icon = stat.icon;
                      return (
                        <span key={i} style={{
                          display: 'flex', alignItems: 'center', gap: '0.3rem',
                          fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)',
                          fontFamily: 'Source Sans Pro, sans-serif',
                        }}>
                          {Icon && <Icon style={{ width: '0.875rem', height: '0.875rem' }} />}
                          {stat.text}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination dots */}
      <div style={{
        display: 'flex', gap: '0.5rem', justifyContent: 'center',
        position: 'absolute', bottom: 0, left: 0, right: 0,
      }}>
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => navigate(i)}
            aria-label={`Ir al testimonio ${i + 1}`}
            style={{
              width: i === activeIndex ? '1.75rem' : '0.5rem',
              height: '0.5rem',
              borderRadius: '0.25rem',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: i === activeIndex
                ? 'linear-gradient(90deg, #7D468C, #4EBFBF)'
                : 'rgba(255,255,255,0.2)',
              padding: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Testimonials Section ────────────────────────────────────────────────
const Testimonials = () => {
  const { t, i18n } = useTranslation();
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API_BASE}/testimonials`)
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          // Merge backend data with defaults (backend may not have glass fields)
          const merged = res.data.map((item, i) => ({
            ...DEFAULT_TESTIMONIALS[i % DEFAULT_TESTIMONIALS.length],
            ...item,
          }));
          setTestimonials(merged);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section
      data-testid="testimonials-section"
      className="py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0F1C2E 0%, #1a2a4a 40%, #0d1825 100%)' }}
    >
      {/* Decorative blobs */}
      <div style={{
        position: 'absolute', top: '-8rem', right: '-8rem',
        width: '28rem', height: '28rem', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(125,70,140,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-6rem', left: '-6rem',
        width: '24rem', height: '24rem', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(78,191,191,0.14) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: '40%', left: '55%',
        width: '16rem', height: '16rem', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(242,120,92,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Geometric accents */}
      <div className="absolute top-8 right-12 w-5 h-5 bg-[#F2785C]/40" style={{ transform: 'rotate(45deg)' }} />
      <div className="absolute bottom-12 left-10 w-3 h-3 bg-[#4EBFBF]/40" style={{ transform: 'rotate(45deg)' }} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-2.5 h-2.5 bg-[#4EBFBF]" style={{ transform: 'rotate(45deg)' }} />
            <span className="text-xs font-semibold text-[#4EBFBF] tracking-widest uppercase font-poppins">
              {t('testimonials.subtitle')}
            </span>
            <div className="w-2.5 h-2.5 bg-[#4EBFBF]" style={{ transform: 'rotate(45deg)' }} />
          </div>
          <h2 className="font-poppins font-bold text-4xl sm:text-5xl text-white leading-tight">
            {t('testimonials.title')}
          </h2>
          <p className="mt-4 text-white/50 font-source-sans text-base max-w-xl mx-auto">
            {lang === 'en'
              ? 'Real stories from clients who trusted us to elevate their brand.'
              : 'Historias reales de clientes que confiaron en nosotros para elevar su marca.'}
          </p>
        </div>

        {/* Glass stack */}
        <TestimonialStack testimonials={testimonials} lang={lang} />
      </div>
    </section>
  );
};

export default Testimonials;
